/**
 * Settings > General > Timezone: the firm's clock. Casey counts "today" and
 * deadlines in it. Owners and admins change it.
 */
import { useMemo, useState, type FC } from "react";
import { toast } from "sonner";

import { LawFirmsAPI } from "@/api/lawFirms";
import type { LawFirm } from "@/api/types/lawFirm";
import { ListCard, ListRow } from "@/components/ui/list-card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  browserTimezone,
  timezoneLabel,
  timezoneOptions
} from "@/features/lawFirms/firmTimezone";

type Props = {
  lawFirm: LawFirm | null;
  onSaved: (firm: LawFirm) => void;
};

export const FirmTimezoneSection: FC<Props> = ({ lawFirm, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const current = lawFirm?.timezone ?? null;
  const options = useMemo(() => timezoneOptions(current), [current]);

  const save = async (zone: string) => {
    if (!lawFirm || zone === current) return;
    setSaving(true);
    try {
      onSaved(await LawFirmsAPI.update(lawFirm.uid, { timezone: zone }));
      toast.success("Timezone saved");
    } catch (error) {
      toast.error("Couldn't save the timezone", {
        description:
          error instanceof Error
            ? error.message
            : "Only owners and admins can change it."
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ListCard>
      <ListRow
        title="Timezone"
        description={
          <span className="text-sm text-fg-3">
            {current
              ? "Casey counts today and deadlines in this timezone."
              : `Not set yet; Casey uses Pacific time. Your browser says ${
                  browserTimezone() ?? "UTC"
                }.`}
          </span>
        }
        control={
          <Select
            value={current ?? ""}
            onValueChange={(v) => void save(v)}
            disabled={!lawFirm || saving}
          >
            <SelectTrigger
              className="w-56"
              aria-label="Firm Timezone"
              data-testid="firm-timezone"
            >
              <SelectValue placeholder="Choose Timezone" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {options.map((zone) => (
                <SelectItem key={zone} value={zone}>
                  {timezoneLabel(zone)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
    </ListCard>
  );
};
